import { useCallback, useSyncExternalStore } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { setFormatLocale } from '@/lib/format';
import { getOverridesVersion, getPreferredLanguage, loadLocale, subscribeOverrides, translate } from './index';

/**
 * Returns `t(key, params?)` bound to the current user's language (from settings),
 * defaulting to Spanish before auth loads. Re-renders when the language changes
 * (reads from the auth query) and when the server-side translation overrides
 * arrive or change (issue #14), via the override-version external store.
 */
export function useT() {
  const { auth } = useAuth();
  const version = useSyncExternalStore(subscribeOverrides, getOverridesVersion, getOverridesVersion);
  // Before the account's settings exist (login, onboarding, demo) fall back to
  // the device/chosen language instead of always Spanish (issue: language step).
  const lang = auth?.settings?.language ?? getPreferredLanguage();
  // Keep date/number formatting (lib/format) in the same language as the UI.
  setFormatLocale(lang);
  // Load this language's strings on demand (no-op once loaded / for es-ca-en).
  void loadLocale(lang);
  return useCallback(
    (key: string, params?: Record<string, string | number>) => translate(lang, key, params),
    // `version` is intentionally in the deps: a new t() identity forces consumers
    // to re-run translate() when overrides change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, version],
  );
}
